<template>
    <div>
        <o-field
            :variant="variant"
            :message="!props.hideValidationMessage && error ? error : ''"
            :class="{
                'has-error': !meta.valid,
            }"
        >
            <template
                v-if="label"
                #label
            >
                {{ props.label }}
                <UiIcon
                    v-if="props.tooltip"
                    class="cursor-pointer absolute ml-1.5 fill-[#90A2BB]"
                    icon="more-info"
                    color=""
                    @click="isActive = !isActive"
                />
            </template>
            <UiNotification v-if="isActive">
                <p v-html="props.tooltip" />
            </UiNotification>
            <slot name="after-label" />
            <o-input
                v-model="value"
                :variant="variant"
                :disabled="props.disabled"
                :type="props.type"
                :icon="props.icon"
                :min="props.minNumeric"
                :max="props.maxNumeric"
                :autofocus="props.autofocus"
                :minlength="props.min"
                :maxlength="props.max"
                :step="props.step"
                :placeholder="props.placeholder"
                :password-reveal="props.passwordReveal"
                :loading="props.loading"
                :number="props.type === 'number'"
                expanded
                @update:model-value="onUpdate"
                @keyup="(e) => emit('keyup', e)"
                @keydown="(e) => emit('keydown', e)"
            />
        </o-field>
        <slot name="after-field" />
    </div>
</template>

<script lang="ts">
    let id = 0;
</script>

<script lang="ts" setup>
    import { RuleExpression, useField, useFieldError } from 'vee-validate';

    import UiNotification from '@/components/Global/UiNotification/UiNotification.vue';

    const props = withDefaults(
        defineProps<{
            min?: number;
            max?: number;
            type?: 'text' | 'number' | 'textarea' | 'password' | 'email' | 'phone' | 'hidden' | 'color';
            icon?: string;
            step?: string;
            name?: string;
            label?: string;
            rules?: RuleExpression<string>;
            value?: string | number;
            tooltip?: string;
            loading?: boolean;
            disabled?: boolean;
            autofocus?: boolean;
            minNumeric?: number;
            maxNumeric?: number;
            modelValue?: string | number;
            placeholder?: string;
            passwordReveal?: boolean;
            hideValidationMessage?: boolean;
        }>(),
        {
            type: 'text',
            rules: 'required',
            autofocus: false,
            hideValidationMessage: false,
            name: () => `ui_input_${++id}`,
        },
    );

    const emit = defineEmits<{
        'update:model-value': [val: string | number | undefined];
        keyup: [val: KeyboardEvent];
        keydown: [val: KeyboardEvent];
    }>();

    const error = useFieldError(props.name);
    const isActive = ref<boolean>(false);
    const rulesRef = ref<RuleExpression<string>>(props.rules);
    const { value, meta } = useField(props.name, rulesRef, { initialValue: String(props.modelValue ?? props.value ?? '') });

    const variant = computed<string>(() => {
        if (props.disabled) {
            return '';
        } else if (!meta.valid && error.value) {
            return 'danger';
        } else if (meta.valid && meta.validated && !props.hideValidationMessage) {
            return 'success';
        } else {
            return '';
        }
    });

    const onUpdate = (newValue: string | number | undefined) => {
        emit('update:model-value', newValue);
    };

    watch(
        () => props.modelValue,
        (newValue: any) => {
            if (newValue !== value.value) {
                value.value = newValue;
            }
        },
    );

    watch(
        () => props.rules,
        (newValue) => (rulesRef.value = newValue),
    );

    defineExpose({ error });
</script>

<style lang="scss" scoped>
    @use '@/assets/scss/imports';

    .notification {
        margin: 0 0 1rem 0 !important;
    }

    :deep(.o-tooltip) {
        cursor: pointer;
        margin-left: imports.$margin-default;
    }

    .tooltip {
        cursor: pointer;
        margin-left: imports.$margin-default;
        flex-shrink: 0;
    }

    :deep(div.control.has-icons-right span.icon) {
        margin-right: 0;
    }

    :deep([data-oruga-input='hidden'] + span.icon) {
        display: none;
    }
</style>
